/**
 * Checkout events for the Meta pixel, sent as the pixel's image beacon (`facebook.com/tr`).
 *
 * Why not `fbevents.js`: the pixel script reports `location.href` with every event, and the links
 * the sales bot sends carry the lead's phone (`?phone=…&sales_token=…`), sometimes email or CPF.
 * Personal data in a URL sent to Meta breaks Meta's terms and LGPD, so the URL is rebuilt here
 * from an allowlist and the beacon carries only that. The `_fbp`/`_fbc` cookies the zenpost.ai
 * pixel set on `.zenpost.ai` go along, so Meta still ties the event to the ad click.
 *
 * Only funnel steps that the pixel does not see today are sent from here: InitiateCheckout
 * (checkout opened) and AddPaymentInfo (PIX/card generated). The purchase itself (Subscribe)
 * is sent to Meta outside this repo; sending it from here too would count every sale twice.
 * Every call is a no-op outside a browser and never throws.
 */

export const META_PIXEL_ID = "1618317328740752";

/** Query parameters that are safe to report: they describe the offer, never the person. */
const SAFE_PARAMS = new Set(["plan", "interval", "app_language", "method"]);
const SAFE_PARAM_PREFIX = "utm_";

export type MetaCheckoutEvent = "InitiateCheckout" | "AddPaymentInfo";

export interface MetaEventData {
  /** Amount in cents, converted to units for Meta. */
  valueCents?: number | null;
  currency?: string;
  /** Fixed codes only, e.g. "premium_monthly". */
  contentName?: string;
  /** e.g. "openpix", "stripe", "stone". */
  paymentType?: string;
}

/** The page URL with every parameter outside the allowlist removed. */
export function sanitizedPageUrl(href: string): string {
  try {
    const url = new URL(href);
    const kept = new URLSearchParams();
    url.searchParams.forEach((value, key) => {
      if (SAFE_PARAMS.has(key) || key.startsWith(SAFE_PARAM_PREFIX)) kept.set(key, value);
    });
    const query = kept.toString();
    return `${url.origin}${url.pathname}${query ? `?${query}` : ""}`;
  } catch {
    return "";
  }
}

function readCookie(name: string, cookie: string): string | undefined {
  for (const part of cookie.split(";")) {
    const [key, ...rest] = part.trim().split("=");
    if (key === name) return rest.join("=") || undefined;
  }
  return undefined;
}

/** The beacon URL for one event; exported so the payload can be tested without a browser. */
export function metaBeaconUrl(
  event: MetaCheckoutEvent,
  data: MetaEventData,
  context: { href: string; cookie: string; eventId?: string; now?: number },
): string {
  const params = new URLSearchParams({ id: META_PIXEL_ID, ev: event, noscript: "1" });
  const dl = sanitizedPageUrl(context.href);
  if (dl) params.set("dl", dl);
  if (context.eventId) params.set("eid", context.eventId);
  params.set("ts", String(context.now ?? Date.now()));
  const fbp = readCookie("_fbp", context.cookie);
  const fbc = readCookie("_fbc", context.cookie);
  if (fbp) params.set("fbp", fbp);
  if (fbc) params.set("fbc", fbc);
  if (typeof data.valueCents === "number" && data.valueCents > 0) {
    params.set("cd[value]", (data.valueCents / 100).toFixed(2));
    params.set("cd[currency]", data.currency ?? "BRL");
  }
  if (data.contentName) params.set("cd[content_name]", data.contentName);
  if (data.paymentType) params.set("cd[payment_type]", data.paymentType);
  return `https://www.facebook.com/tr?${params.toString()}`;
}

export function metaEvent(event: MetaCheckoutEvent, data: MetaEventData = {}, eventId?: string): void {
  try {
    if (typeof window === "undefined" || typeof document === "undefined" || typeof Image === "undefined") return;
    const img = new Image(1, 1);
    img.referrerPolicy = "no-referrer";
    img.src = metaBeaconUrl(event, data, { href: window.location.href, cookie: document.cookie, eventId });
  } catch {
    /* analytics must never break the page */
  }
}
